import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, ChevronRight, Loader2, X } from 'lucide-react';
import {
  ApiError,
  UNAUTHORIZED_EVENT,
  accountHue,
  api,
  type AccountInput,
  type PublicAccount,
  type SessionInfo,
} from '../lib/api';
import { useUiStore } from '../lib/store';
import ConnectAccount from './ConnectAccount';
import styles from './AccountsSettings.module.css';

const GMAIL_APP_PASSWORD_URL = 'https://myaccount.google.com/apppasswords';

function relative(epochSec: number | null): string {
  if (epochSec === null) return 'never';
  const delta = Math.floor(Date.now() / 1000) - epochSec;
  if (delta < 60) return 'just now';
  if (delta < 3600) return `${Math.floor(delta / 60)} min ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)} h ago`;
  return `${Math.floor(delta / 86400)} d ago`;
}

function statusTone(account: PublicAccount): 'ok' | 'warn' | 'bad' | 'neutral' {
  if (!account.enabled || account.status === 'disabled') return 'neutral';
  if (account.status === 'auth_error' || account.status === 'error') return 'bad';
  if (account.status === 'pending' || account.status === 'connecting') return 'warn';
  return 'ok';
}

const STATUS_TEXT: Record<PublicAccount['status'], string> = {
  pending: 'Pending',
  connecting: 'Connecting',
  ok: 'Connected',
  auth_error: 'Sign-in failed',
  error: 'Sync error',
  disabled: 'Paused',
};

/** Derived locally so the picker preselects before the presets query lands. */
function guessPreset(email: string): string {
  const domain = email.split('@')[1]?.toLowerCase() ?? '';
  if (/^(gmail\.com|googlemail\.com)$/.test(domain)) return 'gmail';
  if (/^(icloud\.com|me\.com|mac\.com)$/.test(domain)) return 'icloud';
  if (/^(fastmail\.com|fastmail\.fm)$/.test(domain)) return 'fastmail';
  if (/^(outlook\.com|hotmail\.com|live\.com|msn\.com)$/.test(domain)) return 'outlook';
  if (/^(yahoo\.com|ymail\.com)$/.test(domain)) return 'yahoo';
  return 'gmail';
}

export default function AccountsSettings() {
  const open = useUiStore((s) => s.settingsOpen);
  const setOpen = useUiStore((s) => s.setSettingsOpen);
  if (!open) return null;
  return <SettingsDialog onClose={() => setOpen(false)} />;
}

function SettingsDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDivElement>(null);

  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => api.accounts() });
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });

  const accounts = accountsQuery.data?.accounts ?? [];
  const presets = accountsQuery.data?.presets ?? [];

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const invalidateAccounts = () => queryClient.invalidateQueries({ queryKey: ['accounts'] });

  return (
    <div
      className={styles.scrim}
      data-motion="overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.dialog}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Accounts and settings"
        data-motion="overlay"
      >
        <div className={styles.head}>
          <h2 className={styles.title}>Accounts</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <X size={18} className="lucide" />
          </button>
        </div>

        <div className={styles.body}>
          {/* OAuth first: it is the path that should be taken by default, and
              burying it under the app-password form would invert that. */}
          <ConnectAccount />

          <section className={styles.section}>
            {accountsQuery.isPending && <p className={styles.empty}>Loading…</p>}
            {accountsQuery.isSuccess && accounts.length === 0 && (
              <p className={styles.empty}>No mailboxes yet. Add one below.</p>
            )}
            {accounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                accounts={accounts}
                onChanged={invalidateAccounts}
              />
            ))}
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Add with an app password</h3>
            {/* This form is the fallback, but it sits lower on the page and
                therefore looks like the default. Say plainly where it does not
                work, because both failure modes are unfixable by retyping. */}
            <p className={styles.sectionNote}>
              Only for mailboxes that still accept a password over IMAP. It cannot work for{' '}
              <strong>Microsoft 365</strong> (basic authentication was removed — there is no app
              password to generate), or for a Google account whose admin has turned app passwords
              off. Use <strong>Add an account</strong> above for either.
            </p>
            <AddAccountForm presets={presets} onAdded={invalidateAccounts} />
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Signed-in devices</h3>
            <SessionList sessions={meQuery.data?.sessions ?? []} />
          </section>
        </div>
      </div>
    </div>
  );
}

// --- One account -------------------------------------------------------------

function AccountCard({
  account,
  accounts,
  onChanged,
}: {
  account: PublicAccount;
  accounts: PublicAccount[];
  onChanged: () => void;
}) {
  const [name, setName] = useState(account.displayName);
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(account.status === 'auth_error');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(account.displayName);
  }, [account.displayName]);

  useEffect(() => {
    if (account.status === 'auth_error') setShowSecret(true);
  }, [account.status]);

  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.displayMessage : 'That did not work.');

  const patch = useMutation({
    mutationFn: (input: Parameters<typeof api.updateAccount>[1]) =>
      api.updateAccount(account.id, input),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: fail,
  });

  const reconnect = useMutation({
    mutationFn: (value: string) => api.reconnectAccount(account.id, value),
    onSuccess: () => {
      setError(null);
      setSecret('');
      setShowSecret(false);
      onChanged();
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => api.deleteAccount(account.id),
    onSuccess: onChanged,
    onError: fail,
  });

  const tone = statusTone(account);

  return (
    <div className={styles.account}>
      <div className={styles.accountHead}>
        <span
          className={styles.hueRail}
          style={{ ['--hue' as string]: accountHue(account.id, accounts) }}
          aria-hidden="true"
        />
        <div className={styles.accountMain}>
          <input
            className={styles.nameInput}
            value={name}
            aria-label={`Display name for ${account.email}`}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              const next = name.trim();
              if (next && next !== account.displayName) patch.mutate({ displayName: next });
              else setName(account.displayName);
            }}
          />
          <div className={styles.email}>{account.email}</div>
        </div>
        <span className={styles.status} data-tone={tone === 'neutral' ? undefined : tone}>
          {tone === 'bad' && <AlertTriangle size={11} className="lucide" aria-hidden="true" />}
          {STATUS_TEXT[account.status]}
        </span>
      </div>

      {account.statusDetail && tone === 'bad' && (
        <div className="kiln-banner" data-tone="danger">
          <span>{account.statusDetail}</span>
        </div>
      )}

      {showSecret && (
        <div className={styles.secretRow}>
          <input
            className="kiln-input"
            type="password"
            autoComplete="off"
            placeholder="New app password"
            aria-label={`New app password for ${account.email}`}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && secret) reconnect.mutate(secret);
            }}
          />
          <button
            type="button"
            className="kiln-button"
            data-tone="accent"
            disabled={!secret || reconnect.isPending}
            onClick={() => reconnect.mutate(secret)}
          >
            {reconnect.isPending ? <Loader2 size={14} className="lucide" /> : null}
            Reconnect
          </button>
        </div>
      )}

      {error && (
        <div className="kiln-banner" data-tone="danger">
          <span>{error}</span>
        </div>
      )}

      <div className={styles.accountActions}>
        <span className={styles.meta}>
          Last synced {relative(account.lastSyncAt)} · {account.provider}
        </span>
        <span className={styles.spacer} />
        {!showSecret && (
          <button
            type="button"
            className="kiln-button"
            data-tone="ghost"
            onClick={() => setShowSecret(true)}
          >
            Replace password
          </button>
        )}
        <button
          type="button"
          className="kiln-button"
          data-tone="ghost"
          disabled={patch.isPending}
          onClick={() => patch.mutate({ enabled: !account.enabled })}
        >
          {account.enabled ? 'Pause syncing' : 'Resume syncing'}
        </button>
        {confirmRemove ? (
          <>
            <button
              type="button"
              className="kiln-button"
              data-tone="danger"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Remove permanently
            </button>
            <button
              type="button"
              className="kiln-button"
              data-tone="ghost"
              onClick={() => setConfirmRemove(false)}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            className="kiln-button"
            data-tone="danger"
            onClick={() => setConfirmRemove(true)}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

// --- Add ---------------------------------------------------------------------

type AddPhase =
  | { kind: 'idle' }
  | { kind: 'testing' }
  | { kind: 'saving'; folders: number }
  | { kind: 'error'; message: string };

function AddAccountForm({
  presets,
  onAdded,
}: {
  presets: { key: string; label: string; imapHost: string; imapPort: number; smtpHost: string; smtpPort: number }[];
  onAdded: () => void;
}) {
  const [email, setEmail] = useState('');
  const [secret, setSecret] = useState('');
  const [presetKey, setPresetKey] = useState('gmail');
  const [presetTouched, setPresetTouched] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('');
  const [phase, setPhase] = useState<AddPhase>({ kind: 'idle' });

  const preset = useMemo(
    () => presets.find((p) => p.key === presetKey),
    [presets, presetKey],
  );

  // The domain picks the preset until the user overrides it by hand.
  useEffect(() => {
    if (presetTouched || !email.includes('@')) return;
    setPresetKey(guessPreset(email));
  }, [email, presetTouched]);

  const busy = phase.kind === 'testing' || phase.kind === 'saving';

  function buildInput(): AccountInput {
    const input: AccountInput = { email: email.trim(), secret, preset: presetKey };
    if (advanced) {
      if (imapHost.trim()) input.imapHost = imapHost.trim();
      if (smtpHost.trim()) input.smtpHost = smtpHost.trim();
      const ip = Number(imapPort);
      const sp = Number(smtpPort);
      if (Number.isFinite(ip) && ip > 0) input.imapPort = ip;
      if (Number.isFinite(sp) && sp > 0) input.smtpPort = sp;
    }
    return input;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || !email.trim() || !secret) return;
    const input = buildInput();

    setPhase({ kind: 'testing' });
    let folders: number;
    try {
      const result = await api.testAccount(input);
      folders = result.folders;
    } catch (err) {
      // The server hands back the IMAP server's own words. Paraphrasing them
      // loses the one detail that tells the user what to fix.
      setPhase({
        kind: 'error',
        message:
          err instanceof ApiError
            ? err.displayMessage
            : 'Could not reach that mail server.',
      });
      return;
    }

    setPhase({ kind: 'saving', folders });
    try {
      await api.addAccount(input);
      setEmail('');
      setSecret('');
      setAdvanced(false);
      setImapHost('');
      setImapPort('');
      setSmtpHost('');
      setSmtpPort('');
      setPresetTouched(false);
      setPhase({ kind: 'idle' });
      onAdded();
    } catch (err) {
      setPhase({
        kind: 'error',
        message: err instanceof ApiError ? err.displayMessage : 'Could not save that mailbox.',
      });
    }
  }

  return (
    <form onSubmit={submit}>
      <div className={styles.grid}>
        <div className={styles.field}>
          <label className="kiln-label" htmlFor="add-email">
            Email address
          </label>
          <input
            id="add-email"
            className="kiln-input"
            type="email"
            autoComplete="off"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className="kiln-label" htmlFor="add-secret">
            App password
          </label>
          <input
            id="add-secret"
            className="kiln-input"
            type="password"
            autoComplete="off"
            placeholder="16 characters, no spaces"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
        </div>

        <div className={styles.field}>
          <label className="kiln-label" htmlFor="add-preset">
            Provider
          </label>
          <select
            id="add-preset"
            className={styles.select}
            value={presetKey}
            onChange={(e) => {
              setPresetTouched(true);
              setPresetKey(e.target.value);
            }}
          >
            {presets.length === 0 && <option value="gmail">Gmail / Google Workspace</option>}
            {presets.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.disclosure}>
        <button
          type="button"
          className={styles.disclosureButton}
          aria-expanded={advanced}
          onClick={() => setAdvanced((v) => !v)}
        >
          <ChevronRight
            size={14}
            className={`lucide ${styles.chevron}`}
            data-open={advanced}
            aria-hidden="true"
          />
          Advanced — server addresses
        </button>

        {advanced && (
          <div className={`${styles.grid} ${styles.disclosurePanel}`}>
            <div className={styles.field}>
              <label className="kiln-label" htmlFor="add-imap-host">
                IMAP host
              </label>
              <input
                id="add-imap-host"
                className="kiln-input"
                placeholder={preset?.imapHost ?? 'imap.example.com'}
                value={imapHost}
                onChange={(e) => setImapHost(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label className="kiln-label" htmlFor="add-imap-port">
                IMAP port
              </label>
              <input
                id="add-imap-port"
                className="kiln-input"
                inputMode="numeric"
                placeholder={String(preset?.imapPort ?? 993)}
                value={imapPort}
                onChange={(e) => setImapPort(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label className="kiln-label" htmlFor="add-smtp-host">
                SMTP host
              </label>
              <input
                id="add-smtp-host"
                className="kiln-input"
                placeholder={preset?.smtpHost ?? 'smtp.example.com'}
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
              />
            </div>
            <div className={styles.field}>
              <label className="kiln-label" htmlFor="add-smtp-port">
                SMTP port
              </label>
              <input
                id="add-smtp-port"
                className="kiln-input"
                inputMode="numeric"
                placeholder={String(preset?.smtpPort ?? 465)}
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {phase.kind === 'error' && (
        <div className="kiln-banner" data-tone="danger" style={{ marginTop: 'var(--sp-6)' }}>
          <AlertTriangle size={15} className="lucide" aria-hidden="true" />
          <span>{phase.message}</span>
        </div>
      )}

      <div className={styles.formActions}>
        <button
          type="submit"
          className="kiln-button"
          data-tone="accent"
          disabled={busy || !email.trim() || !secret}
        >
          {busy && <Loader2 size={14} className="lucide" aria-hidden="true" />}
          {phase.kind === 'testing'
            ? 'Testing sign-in…'
            : phase.kind === 'saving'
              ? 'Adding…'
              : 'Test and add'}
        </button>
        {phase.kind === 'saving' && (
          <span className={styles.meta}>
            <Check size={12} className="lucide" aria-hidden="true" /> Signed in ·{' '}
            {phase.folders} folders
          </span>
        )}
      </div>

      {presetKey === 'gmail' && (
        <div className={styles.help}>
          <p>
            Gmail and Google Workspace need an <strong>app password</strong>, not your normal
            one. Create it at{' '}
            <a href={GMAIL_APP_PASSWORD_URL} target="_blank" rel="noreferrer noopener">
              myaccount.google.com/apppasswords
            </a>
            . The page only appears once 2-Step Verification is switched on for the account.
          </p>
          <p>
            Paste the 16 characters without the spaces Google shows. Changing the Google
            account password revokes every app password, so this mailbox will stop syncing
            until you generate a new one and reconnect here.
          </p>
        </div>
      )}
    </form>
  );
}

// --- Sessions ----------------------------------------------------------------

function SessionList({ sessions }: { sessions: SessionInfo[] }) {
  const [busy, setBusy] = useState(false);

  async function signOutEverywhere() {
    setBusy(true);
    try {
      await api.revokeAll();
    } finally {
      setBusy(false);
      // revoke-all kills this session too; go straight to the login screen
      // rather than letting the next request 401 on its own.
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
    }
  }

  return (
    <>
      {sessions.length === 0 ? (
        <p className={styles.empty}>No other devices.</p>
      ) : (
        sessions.map((s) => (
          <div key={s.id} className={styles.session}>
            <div className={styles.sessionMain}>
              <div>{s.label ?? 'Unlabelled device'}</div>
              <div className={styles.meta}>
                {s.ip ?? 'unknown address'} · active {relative(s.last_seen)}
              </div>
            </div>
          </div>
        ))
      )}
      <div className={styles.formActions}>
        <button
          type="button"
          className="kiln-button"
          data-tone="danger"
          disabled={busy}
          onClick={signOutEverywhere}
        >
          Sign out everywhere
        </button>
      </div>
    </>
  );
}
